import { EventEmitter, Injectable, TemplateRef, Type } from '@angular/core';
import { ComponentLoader, ComponentLoaderFactory } from 'ngx-bootstrap/component-loader';
import { SettingsSidePanel } from './settings-side-panel';
import { SettingsSidePanelDefaults, SettingsSidePanelOptions } from './settings-side-panel.options';

export interface SettingsSidePanelRef {
  hide: () => void;
  onHidden: EventEmitter<unknown>;
}

let currentId = 6001;

/**
 * Opens `SettingsSidePanel`. Intentionally does not extend `AbstractPanelService` — that base
 * class exists to support backdrop management and the `ButtonConfig`/`StandardPanelTemplate`
 * footer wiring the legacy side/full/medium panels share, neither of which this panel has.
 * This is a small, self-contained service using the same `ComponentLoaderFactory` primitive the
 * legacy panels use, so nothing here depends on or modifies any existing panel file.
 */
@Injectable({
  providedIn: 'root',
})
export class SettingsSidePanelService {
  private loaders: ComponentLoader<SettingsSidePanel>[] = [];

  constructor(private clf: ComponentLoaderFactory) {}

  show<T>(
    content: string | TemplateRef<unknown> | Type<T>,
    config?: SettingsSidePanelOptions<T>,
  ): SettingsSidePanelRef {
    const id = config?.id ?? currentId++;
    // `SettingsSidePanelDefaults` is a fixed, non-generic object (default `initialState` shape),
    // so spreading it alongside the caller's `SettingsSidePanelOptions<T>` makes TS try to
    // reconcile two different `initialState` shapes into one literal — cast instead of fighting
    // that; the values themselves are correct, this is purely a type-inference artifact.
    const panelConfig = {
      ...SettingsSidePanelDefaults,
      ...config,
      id,
      content,
    } as SettingsSidePanelOptions<T>;

    const loader = this.clf.createLoader<SettingsSidePanel>();
    this.loaders.push(loader);

    if (panelConfig.providers) {
      for (const provider of panelConfig.providers) {
        loader.provide(provider);
      }
    }

    loader
      .provide({ provide: SettingsSidePanelOptions, useValue: panelConfig })
      .attach(SettingsSidePanel)
      .to(panelConfig.appendTo || 'body')
      .show({});

    const ref: SettingsSidePanelRef = {
      hide: () => this.hide(id),
      onHidden: new EventEmitter<unknown>(),
    };
    loader.onHidden.subscribe((data: unknown) => ref.onHidden.emit(data));

    return ref;
  }

  /**
   * Public close entry point for external callers (e.g. a Save/Cancel button in the panel's
   * projected content). Defers to the component's own `close()` so external callers get the
   * same animated slide-out as clicking the panel's own close button, instead of an instant
   * teardown — `close()` calls back into `remove()` below once its animation finishes.
   */
  hide(id?: number | string): void {
    const loader = this.findLoader(id);
    loader?.instance?.close();
  }

  /** Called by `SettingsSidePanel.close()` once its slide-out animation has finished — not
   *  meant to be called directly; use `hide()` instead. */
  remove(id?: number | string): void {
    const index = this.loaders.findIndex((loader) => (loader.instance?.config as any)?.id === id);
    const loader = index >= 0 ? this.loaders[index] : this.loaders[this.loaders.length - 1];
    if (!loader) {
      return;
    }
    loader.hide(id);
    if (index >= 0) {
      this.loaders.splice(index, 1);
    } else {
      this.loaders.pop();
    }
  }

  private findLoader(id?: number | string): ComponentLoader<SettingsSidePanel> | undefined {
    const index = this.loaders.findIndex((loader) => (loader.instance?.config as any)?.id === id);
    return index >= 0 ? this.loaders[index] : this.loaders[this.loaders.length - 1];
  }
}
